<template>
  <div class="flex-1 flex flex-col justify-between p-6">
    <div class="divide-y divide-gray-200">
      <div class="space-y-6 pt-6 pb-5">
        <div class="flex w-full items-center mb-12">
          <div class="dark:text-white text-lg flex">
            <ArrowLeft
              class="self-center h-5 w-5 text-gray-600 cursor-pointer"
              @click="
                () => {
                  $router.push(`/workspace/${$route.params.org}/plans`)
                }
              " />
            <div class="ml-4 dark:text-white">
              {{ $t("Go to all shared plans") }}
            </div>
          </div>
          <ToggleStatusButton :disable-toggle="true" :object="plan" class="ml-auto" />
        </div>

        <div class="rounded-md shadow-sm -space-y-px mt-10">
          <TextInput
            ref="planName"
            :value="plan.name"
            :disabled="true"
            name="plan_name"
            :auto-focus="true"
            :is-box-background="true"
            type="text"
            :label="$t('Plan name')"
            input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm" />
        </div>
        <div class="rounded-md border-0.5 dark:border-dark-divider-border h-28 mt-5">
          <TextEditor
            :value="plan.description"
            :disabled="true"
            toolbar="essential"
            name="description"
            :label="$t('Description')"
            theme="bubble" />
        </div>

        <div class="flex flex-row gap-2 justify-between items-center">
          <SelectInput
            name="type"
            :value="plan.type"
            :disabled="true"
            :items="types"
            label-attr="label"
            :allow-null="false"
            :classes="'w-full'"
            value-attr="value"
            :label="$t('Plan type')"
            :show-label="true"
            :is-box-background="true"
            input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm" />
          <div class="relative w-full">
            <label
              for="d_date"
              class="px-1 h-fit z-10 block text-sm dark:text-dark-search-bar-text dark:text-dark-body-text absolute rounded-full transition-all -translate-y-1/2 left-2 ease-in-out duration-side-menu-transition-duration"
              :class="[
                inputIsFocused || !!due_date ? 'top-0' : 'top-1/2',
                isBoxBackgroud
                  ? 'bg-light-box-background dark:bg-dark-box-background'
                  : 'bg-white text-gray-400 dark:bg-dark-box-background',
                inputIsFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
              ]">{{ $t("Due date") }}</label>
            <div class="rounded-md shadow-sm">
              <DatePicker
                v-model="due_date"
                :is-dark="$store.state.ui.darkMode"
                class="bg-white dark:bg-dark-screen-background"
                is-inline
                mode="date"
                :popover="{ visibility: 'click' }">
                <template #default="{ inputValue, inputEvents }">
                  <div class="relative">
                    <svg
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      class="text-gray-600 w-4 h-full mx-2 absolute pointer-events-none">
                      <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>

                    <input
                      id="d_date"
                      :disabled="true"
                      autocomplete="off"
                      :value="inputValue"
                      class="pl-8 pr-2 border rounded-lg w-full px-10 h-10 text-light-body-text dark:text-dark-body-text outline-none border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue transition ease-in-out duration-side-menu-transition-duration"
                      :class="[
                        !isBoxBackgroud
                          ? 'bg-light-box-background dark:bg-dark-box-background'
                          : 'bg-light-screen-background dark:bg-dark-box-background'
                      ]"
                      @focusin="focusInput"
                      @focusout="focusInput"
                      v-on="inputEvents"
                      @input="
                        $utils.debounce(() => {
                          due_date = $event.target.value
                        }, 800)
                      ">
                  </div>
                </template>
              </DatePicker>
            </div>
          </div>
        </div>
        <div v-if="selected_companies.length">
          <div class="w-[800px] mt-3 mb-5">
            <CompaniesSelect :disabled="true" :label="$t('Companies/Opportunities')" :companies="selected_companies" />
          </div>
        </div>

        <div class="flex w-full">
          <div v-if="selected_team_members.length" class="w-1/2 mt-3 mb-5">
            <div class="w-1/2 mt-3 mb-5">
              <TeamMembersSelect v-model:selected-team-members="selected_team_members" :disabled="true" :label="$t('Team members')" />
            </div>
          </div>
          <div v-if="selected_contacts.length" class="w-1/2 mt-3 mb-5">
            <ContactsSelect
              v-model:selected-contacts="selected_contacts"
              :disabled="true"
              :companies="selected_companies"
              :label="$t('Contacts')"
              :types="['contacts']" />
          </div>
        </div>

        <div class="flex gap-x-2 items-center">
          <label class="text-common-grey relative top-4">{{ $t("Assigned goals") }}:</label>
        </div>

        <div
          v-if="!related_goals_displayed.length"
          class="relative top-4 p-4 border dark:border-dark-divider-border rounded-lg w-full text-common-grey text-center">
          <div class="text-lg">
            {{ $t("No goals to display") }}
          </div>
          <div class="text-sm mt-1">
            {{ $t("This plan has no goals") }}
          </div>
        </div>
        <div :class="[`grid ${full_screen ? 'grid-cols-2' : 'grid-cols-1'} gap-x-8`]">
          <div>
            <div v-for="goal in related_goals_displayed.slice(0, Math.ceil(related_goals_displayed.length / 2))" :key="goal.id">
              <div>
                <div
                  v-if="expanded_goals.includes(goal.id)"
                  class="mb-4 bg-white dark:bg-dark-box-background relative p-4 dark:border-dark-divider-border border rounded-md dark:text-white"
                  @mouseenter="handleGoalEnter(goal)"
                  @mouseleave="handleGoalLeave">
                  <div class="flex items-center">
                    <div
                      :class="[
                        `shadow-lg ${
                          goal.status == 'pending' &&
                          $moment.utc(goal.due_date ? goal.due_date : '3000-01-01').format('YYYY-MM-DD') >=
                          $moment
                            .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                            .format('YYYY-MM-DD')
                            ? 'bg-status-warning-yellow'
                            : goal.status == 'completed'
                              ? 'bg-status-success-green'
                              : goal.status == 'missed' ||
                                (goal.status == 'pending' &&
                                  $moment.utc(goal.due_date ? goal.due_date : '3000-01-01').format('YYYY-MM-DD') <
                                  $moment
                                    .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                                    .format('YYYY-MM-DD'))
                                ? 'bg-status-error-red'
                                : 'bg-dark-brand-blue'
                        } p-2 px-8  rounded-full mt-1 mr-4 relative`
                      ]">
                      <Menu as="div" class="flex-shrink-0 relative">
                        <MenuButton
                          :class="[
                            `${
                              goal.status == 'pending' &&
                              $moment.utc(goal.due_date ? goal.due_date : '3000-01-01').format('YYYY-MM-DD') >=
                              $moment
                                .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                                .format('YYYY-MM-DD')
                                ? 'text-common-grey'
                                : 'text-white'
                            }`
                          ]"
                          class="inline-flex w-24 items-center justify-center border-1 rounded-full focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
                          <span class="capitalize">{{
                            goal.status == "pending"
                              ? $moment.utc(goal.due_date ? goal.due_date : "3000-01-01").format("YYYY-MM-DD") <
                                $moment
                                  .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                                  .format("YYYY-MM-DD")
                                ? "Overdue"
                                : "Pending"
                              : goal.status
                          }}</span>
                        </MenuButton>
                      </Menu>
                    </div>
                    <div>{{ goal.name }}</div>
                    <div class="absolute -right-3 -top-3 flex gap-x-2">
                      <div
                        class="has-tooltip cursor-pointer w-7 h-7 dark:border-dark-divider-border bg-white rounded-full border dark:bg-dark-screen-background"
                        @click="handleExpandGoal(goal)">
                        <div class="w-[15px] h-[15px] ml-[5px] mt-[0px] border-b-[3px] border-b-[#45D72D]" />
                        <span
                          class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                          {{ $t("Minimize") }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="flex mt-5 w-full">
                    <div class="w-2/3 mt-2">
                      <div class="flex">
                        <div class="flex gap-x-2">
                          <div v-if="goal?.filter" class="text-common-grey">
                            {{ $t("Progress") }}:
                          </div>
                          <div v-else class="text-common-grey text-sm">
                            {{ $t("No filter specified for this goal") }}
                          </div>
                          <div v-if="goal?.filter" class="text-md dark:text-white font-light">
                            {{
                              goal?.record.values[goal.filter.split(" ")[1]]
                                ? $utils.truncateNumber(parseInt(goal?.record.values[goal.filter.split(" ")[1]]))
                                : 0
                            }}/{{ $utils.truncateNumber(parseInt(goal.filter.split(" ")[goal.filter.split(" ").length - 2])) }}
                            {{ $utils.getMetricByReferenceName(goal.filter.split(" ")[1])?.name }}
                          </div>
                        </div>
                        <div v-if="goal?.record?.values[goal.filter?.split(' ')[1]]" class="ml-auto text-lg">
                          {{
                            Math.round(
                              (parseInt(goal?.record.values[goal.filter.split(" ")[1]]) /
                                parseInt(goal.filter.split(" ")[goal.filter.split(" ").length - 2])) *
                                100
                            )
                          }}%
                        </div>
                      </div>
                      <div v-if="goal.filter" class="py-4 -mt-3 whitespace-nowrap text-sm text-gray-500">
                        <div class="w-full rounded-full bg-gray-300 dark:bg-dark-box-background h-2">
                          <div
                            :style="{
                              width: `${
                                (goal?.record.values[goal.filter.split(' ')[1]] ? goal?.record.values[goal.filter.split(' ')[1]] : 0) /
                                goal.filter.split(' ')[goal.filter.split(' ').length - 2] >
                                1
                                  ? 100
                                  : `${
                                    (goal?.record.values[goal.filter.split(' ')[1]]
                                      ? goal?.record.values[goal.filter.split(' ')[1]]
                                      : 0) / goal.filter.split(' ')[goal.filter.split(' ').length - 2]
                                  }` * 100
                              }%`
                            }"
                            :class="[`rounded-full bg-blue h-2`]" />
                        </div>
                      </div>
                    </div>
                    <div class="ml-auto">
                      <div class="flex flex-row gap-2 items-center">
                        <div class="text-common-grey">
                          {{ $t("Due Date") }}:
                        </div>
                        <div class="w-1/12 items-start flex justify-start text-start py-2 whitespace-nowrap text-md">
                          <span v-if="goal.due_date">
                            {{ $moment(goal.due_date).format("DD MMM  YYYY") }}
                          </span>
                          <span v-else> - </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-else
                  class="mb-4 bg-white dark:bg-dark-box-background flex align-middle relative p-4 dark:border-dark-divider-border border rounded-md dark:text-white"
                  @mouseenter="handleGoalEnter(goal)"
                  @mouseleave="handleGoalLeave">
                  <div
                    :class="[
                      `${
                        goal.status == 'pending' &&
                        $moment.utc(goal.due_date ? goal.due_date : '3000-01-01').format('YYYY-MM-DD') >=
                        $moment
                          .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                          .format('YYYY-MM-DD')
                          ? 'bg-status-warning-yellow'
                          : goal.status == 'completed'
                            ? 'bg-status-success-green'
                            : goal.status == 'missed' ||
                              (goal.status == 'pending' &&
                                $moment.utc(goal.due_date ? goal.due_date : '3000-01-01').format('YYYY-MM-DD') <
                                $moment
                                  .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                                  .format('YYYY-MM-DD'))
                              ? 'bg-status-error-red'
                              : 'bg-dark-brand-blue'
                      }  w-4 h-4 rounded-full mt-1 mr-4`
                    ]" />
                  <div>{{ goal.name }}</div>
                  <div class="ml-auto mr-20">
                    <div v-if="goal?.record?.values[goal.filter?.split(' ')[1]]" class="ml-auto text-md text-common-grey">
                      {{
                        Math.round(
                          (parseInt(goal?.record.values[goal.filter.split(" ")[1]]) /
                            parseInt(goal.filter.split(" ")[goal.filter.split(" ").length - 2])) *
                            100
                        )
                      }}%
                    </div>
                  </div>
                  <div class="absolute -right-3 -top-3 flex gap-x-2">
                    <div
                      class="has-tooltip cursor-pointer w-7 h-7 dark:border-dark-divider-border bg-white rounded-full border dark:bg-dark-screen-background"
                      @click="handleExpandGoal(goal)">
                      <div class="w-[15px] h-[15px] ml-[5px] mt-[5px] border-[3px] rounded-md border-[#45D72D]" />
                      <span
                        class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                        {{ $t("Expand") }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div
              v-for="goal in related_goals_displayed.slice(Math.round(related_goals_displayed.length / 2), related_goals_displayed.length)"
              :key="goal.id">
              <div>
                <div
                  v-if="expanded_goals.includes(goal.id)"
                  class="mb-4 bg-white dark:bg-dark-box-background relative p-4 dark:border-dark-divider-border border rounded-md dark:text-white"
                  @mouseenter="handleGoalEnter(goal)"
                  @mouseleave="handleGoalLeave">
                  <div class="flex items-center">
                    <div
                      :class="[
                        `shadow-lg ${
                          goal.status == 'pending' &&
                          $moment.utc(goal.due_date ? goal.due_date : '3000-01-01').format('YYYY-MM-DD') >=
                          $moment
                            .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                            .format('YYYY-MM-DD')
                            ? 'bg-status-warning-yellow'
                            : goal.status == 'completed'
                              ? 'bg-status-success-green'
                              : goal.status == 'missed' ||
                                (goal.status == 'pending' &&
                                  $moment.utc(goal.due_date ? goal.due_date : '3000-01-01').format('YYYY-MM-DD') <
                                  $moment
                                    .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                                    .format('YYYY-MM-DD'))
                                ? 'bg-status-error-red'
                                : 'bg-dark-brand-blue'
                        } p-2 px-8  rounded-full mt-1 mr-4 relative`
                      ]">
                      <Menu as="div" class="flex-shrink-0 relative">
                        <MenuButton
                          :class="[`${goal.status == 'pending' ? 'text-common-grey' : 'text-white'}`]"
                          class="inline-flex w-24 items-center justify-center border-1 rounded-full focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
                          <span class="capitalize">{{
                            goal.status == "pending"
                              ? $moment.utc(goal.due_date ? goal.due_date : "3000-01-01").format("YYYY-MM-DD") <
                                $moment
                                  .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                                  .format("YYYY-MM-DD")
                                ? "Overdue"
                                : "Pending"
                              : goal.status
                          }}</span>
                        </MenuButton>
                      </Menu>
                    </div>
                    <div>{{ goal.name }}</div>
                    <div class="absolute -right-3 -top-3 flex gap-x-2">
                      <div
                        class="has-tooltip cursor-pointer w-7 h-7 dark:border-dark-divider-border bg-white rounded-full border dark:bg-dark-screen-background"
                        @click="handleExpandGoal(goal)">
                        <div class="w-[15px] h-[15px] ml-[5px] mt-[0px] border-b-[3px] border-b-[#45D72D]" />
                        <span
                          class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                          {{ $t("Minimize") }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="flex mt-5 w-full">
                    <div class="w-2/3 mt-2">
                      <div class="flex">
                        <div class="flex gap-x-2">
                          <div v-if="goal?.filter" class="text-common-grey">
                            {{ $t("Progress") }}:
                          </div>
                          <div v-else class="text-common-grey text-sm">
                            {{ $t("No filter specified for this goal") }}
                          </div>
                          <div v-if="goal?.filter" class="text-md dark:text-white font-light">
                            {{
                              goal?.record.values[goal.filter.split(" ")[1]]
                                ? $utils.truncateNumber(parseInt(goal?.record.values[goal.filter.split(" ")[1]]))
                                : 0
                            }}/{{ $utils.truncateNumber(parseInt(goal.filter.split(" ")[goal.filter.split(" ").length - 2])) }}
                            {{ $utils.getMetricByReferenceName(goal.filter.split(" ")[1])?.name }}
                          </div>
                        </div>
                        <div v-if="goal?.record?.values[goal.filter?.split(' ')[1]]" class="ml-auto text-lg">
                          {{
                            Math.round(
                              (parseInt(goal?.record.values[goal.filter.split(" ")[1]]) /
                                parseInt(goal.filter.split(" ")[goal.filter.split(" ").length - 2])) *
                                100
                            )
                          }}%
                        </div>
                      </div>
                      <div v-if="goal.filter" class="py-4 -mt-3 whitespace-nowrap text-sm text-gray-500">
                        <div class="w-full rounded-full bg-gray-300 dark:bg-dark-box-background h-2">
                          <div
                            :style="{
                              width: `${
                                (goal?.record.values[goal.filter.split(' ')[1]] ? goal?.record.values[goal.filter.split(' ')[1]] : 0) /
                                goal.filter.split(' ')[goal.filter.split(' ').length - 2] >
                                1
                                  ? 100
                                  : `${
                                    (goal?.record.values[goal.filter.split(' ')[1]]
                                      ? goal?.record.values[goal.filter.split(' ')[1]]
                                      : 0) / goal.filter.split(' ')[goal.filter.split(' ').length - 2]
                                  }` * 100
                              }%`
                            }"
                            :class="[` rounded-full bg-blue h-2`]" />
                        </div>
                      </div>
                    </div>
                    <div class="ml-auto">
                      <div class="flex flex-row gap-2 items-center">
                        <div class="text-common-grey">
                          {{ $t("Due Date") }}:
                        </div>
                        <div class="w-1/12 items-start flex justify-start text-start py-2 whitespace-nowrap text-md">
                          <span v-if="goal.due_date">
                            {{ $moment(goal.due_date).format("DD MMM  YYYY") }}
                          </span>
                          <span v-else> - </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-else
                  class="mb-4 flex align-middle relative bg-white dark:bg-dark-box-background p-4 dark:border-dark-divider-border border rounded-md dark:text-white"
                  @mouseenter="handleGoalEnter(goal)"
                  @mouseleave="handleGoalLeave">
                  <div
                    :class="[
                      `${
                        goal.status == 'pending' &&
                        $moment.utc(goal.due_date ? goal.due_date : '3000-01-01').format('YYYY-MM-DD') >=
                        $moment
                          .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                          .format('YYYY-MM-DD')
                          ? 'bg-status-warning-yellow'
                          : goal.status == 'completed'
                            ? 'bg-status-success-green'
                            : goal.status == 'missed' ||
                              (goal.status == 'pending' &&
                                $moment.utc(goal.due_date ? goal.due_date : '3000-01-01').format('YYYY-MM-DD') <
                                $moment
                                  .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                                  .format('YYYY-MM-DD'))
                              ? 'bg-status-error-red'
                              : 'bg-dark-brand-blue'
                      }  w-4 h-4 rounded-full mt-1 mr-4`
                    ]" />
                  <div>{{ goal.name }}</div>
                  <div class="ml-auto mr-20">
                    <div v-if="goal?.record?.values[goal.filter?.split(' ')[1]]" class="ml-auto text-md text-common-grey">
                      {{
                        Math.round(
                          (parseInt(goal?.record.values[goal.filter.split(" ")[1]]) /
                            parseInt(goal.filter.split(" ")[goal.filter.split(" ").length - 2])) *
                            100
                        )
                      }}%
                    </div>
                  </div>
                  <div class="absolute -right-3 -top-3 flex gap-x-2">
                    <div
                      class="has-tooltip cursor-pointer w-7 h-7 dark:border-dark-divider-border bg-white rounded-full border dark:bg-dark-screen-background"
                      @click="handleExpandGoal(goal)">
                      <div class="w-[15px] h-[15px] ml-[5px] mt-[5px] border-[3px] rounded-md border-[#45D72D]" />
                      <span
                        class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                        {{ $t("Expand") }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="w-full flex items-center">
          <div v-if="goals_count > 6" class="text-common-grey mr-2 text-sm">
            {{ $t("Show") }}
          </div>
          <SelectInput
            v-if="goals_count > 6"
            :value="perPage"
            :is-box-background="true"
            class="w-[115px]"
            :input-classes="'text-left'"
            name="page_show_options"
            :allow-null="false"
            :items="page_show_options"
            @update:currentItem="perPage = $event" />
          <div v-if="goals_count > 6" class="text-common-grey ml-2 text-sm">
            {{ $t("in page") }}
          </div>
          <Pagination
            v-if="goals_count > perPage"
            class="ml-auto"
            :total-pages="total_pages"
            :current-page="currentPage"
            @input="changePage" />
        </div>
        <div class="flex gap-x-2 items-center">
          <label class="text-common-grey relative top-4">{{ $t("Assigned tasks") }}:</label>
        </div>
        <div
          v-if="!related_tasks_displayed.length"
          class="relative top-4 p-4 border dark:border-dark-divider-border rounded-lg w-full text-common-grey text-center">
          <div class="text-lg">
            {{ $t("No tasks to display") }}
          </div>
          <div class="text-sm mt-1">
            {{ $t("This plan has no tasks") }}
          </div>
        </div>
        <div :class="[`grid ${full_screen ? 'grid-cols-2' : 'grid-cols-1'} gap-x-8`]">
          <div>
            <div v-for="task in related_tasks_displayed.slice(0, Math.ceil(related_tasks_displayed.length / 2))" :key="task.id">
              <div
                v-if="expanded_tasks.includes(task.id)"
                class="mb-4 bg-white dark:bg-dark-box-background relative p-4 dark:border-dark-divider-border border rounded-md dark:text-white"
                @mouseenter="handleTaskEnter(task)"
                @mouseleave="handleTaskLeave">
                <div class="flex items-center">
                  <div
                    :class="[
                      `shadow-lg ${
                        task.status == 'pending' &&
                        $moment.utc(task.due_date ? task.due_date : '3000-01-01').format('YYYY-MM-DD') >=
                        $moment
                          .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                          .format('YYYY-MM-DD')
                          ? 'bg-status-warning-yellow'
                          : task.status == 'completed'
                            ? 'bg-status-success-green'
                            : task.status == 'cancelled' ||
                              (task.status == 'pending' &&
                                $moment.utc(task.due_date ? task.due_date : '3000-01-01').format('YYYY-MM-DD') <
                                $moment
                                  .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                                  .format('YYYY-MM-DD'))
                              ? 'bg-status-error-red'
                              : 'bg-dark-brand-blue'
                      } p-2 px-8  rounded-full mt-1 mr-4 relative`
                    ]">
                    <Menu as="div" class="flex-shrink-0 relative">
                      <MenuButton
                        :class="[
                          `${
                            task.status == 'pending' &&
                            $moment.utc(task.due_date ? task.due_date : '3000-01-01').format('YYYY-MM-DD') >=
                            $moment
                              .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                              .format('YYYY-MM-DD')
                              ? 'text-common-grey'
                              : 'text-white'
                          }`
                        ]"
                        class="inline-flex w-24 items-center justify-center border-1 rounded-full focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
                        <span class="capitalize">{{
                          task.status == "pending"
                            ? $moment.utc(task.due_date ? task.due_date : "3000-01-01").format("YYYY-MM-DD") <
                              $moment
                                .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                                .format("YYYY-MM-DD")
                              ? "Overdue"
                              : "Pending"
                            : task.status
                        }}</span>
                      </MenuButton>
                    </Menu>
                  </div>
                  <div>{{ task.name }}</div>
                  <div class="absolute -right-3 -top-3 flex gap-x-2">
                    <div
                      class="has-tooltip cursor-pointer w-7 h-7 dark:border-dark-divider-border bg-white rounded-full border dark:bg-dark-screen-background"
                      @click="handleExpandTask(task)">
                      <div class="w-[15px] h-[15px] ml-[5px] mt-[0px] border-b-[3px] border-b-[#45D72D]" />
                      <span
                        class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                        {{ $t("Minimize") }}</span>
                    </div>
                  </div>
                </div>
                <div class="flex mt-5 w-full justify-between">
                  <div>
                    <div class="flex flex-row gap-2 items-center">
                      <div class="text-common-grey">
                        {{ $t("Priority") }}:
                      </div>
                      <div class="w-1/12 items-start flex justify-start text-start py-2 whitespace-nowrap text-md">
                        <div v-if="task.priority" class="capitalize flex gap-x-2 items-center">
                          <div>
                            {{ task.priority }}
                          </div>
                          <div>
                            <div v-if="task.priority == 'normal'">
                              <div :class="[`border-b-2 w-3  border-status-warning-yellow`]" />
                              <div :class="[`border-b-2 w-3 mt-1 border-status-warning-yellow`]" />
                            </div>
                            <div v-else-if="task.priority == 'high'">
                              <ChevronRight class="h-5 w-5 -rotate-90 text-status-error-red" />
                            </div>
                            <div v-else-if="task.priority == 'highest'">
                              <ChevronsRight class="h-5 w-5 -rotate-90 text-status-error-red" />
                            </div>
                            <div v-else-if="task.priority == 'low'">
                              <ChevronRight class="h-5 w-5 rotate-90 text-status-success-green" />
                            </div>
                            <div v-else-if="task.priority == 'lowest'">
                              <ChevronsRight class="h-5 w-5 rotate-90 text-status-success-green" />
                            </div>
                          </div>
                        </div>
                        <span v-else> - </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div class="flex flex-row gap-2 items-center">
                      <div class="text-common-grey">
                        {{ $t("Due Date") }}:
                      </div>
                      <div class="w-1/12 items-start flex justify-start text-start py-2 whitespace-nowrap text-md">
                        <span v-if="task.due_date">
                          {{ $moment(task.due_date).format("DD MMM  YYYY") }}
                        </span>
                        <span v-else> - </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div class="flex flex-row gap-2 items-center">
                      <div class="text-common-grey">
                        {{ $t("Related Goal") }}:
                      </div>
                      <div v-if="task.goal" class="text-md w-24 overflow-hidden overflow-ellipsis whitespace-nowrap">
                        {{ $store.state.data.goals.results.find((g) => g.id == task.goal)?.name }}
                      </div>
                      <div v-else>
                        -
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="mb-4 flex align-middle bg-white dark:bg-dark-box-background relative p-4 dark:border-dark-divider-border border rounded-md dark:text-white"
                @mouseenter="handleTaskEnter(task)"
                @mouseleave="handleTaskLeave">
                <div
                  :class="[
                    `${
                      task.status == 'pending' &&
                      $moment.utc(task.due_date ? task.due_date : '3000-01-01').format('YYYY-MM-DD') >=
                      $moment.utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`).format('YYYY-MM-DD')
                        ? 'bg-status-warning-yellow'
                        : task.status == 'completed'
                          ? 'bg-status-success-green'
                          : task.status == 'cancelled' ||
                            (task.status == 'pending' &&
                              $moment.utc(task.due_date ? task.due_date : '3000-01-01').format('YYYY-MM-DD') <
                              $moment
                                .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                                .format('YYYY-MM-DD'))
                            ? 'bg-status-error-red'
                            : 'bg-dark-brand-blue'
                    }  w-4 h-4 rounded-full mt-1 mr-4`
                  ]" />
                <div>{{ task.name }}</div>
                <div class="absolute -right-3 -top-3 flex gap-x-2">
                  <div
                    class="has-tooltip cursor-pointer w-7 h-7 dark:border-dark-divider-border bg-white rounded-full border dark:bg-dark-screen-background"
                    @click="handleExpandTask(task)">
                    <div class="w-[15px] h-[15px] ml-[5px] mt-[5px] border-[3px] rounded-md border-[#45D72D]" />
                    <span
                      class="tooltip whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md text-sm bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                      {{ $t("Expand") }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div v-for="task in related_tasks_displayed.slice(Math.round(related_tasks_displayed.length / 2), length)" :key="task.id">
              <div
                v-if="expanded_tasks.includes(task.id)"
                class="mb-4 relative bg-white dark:bg-dark-box-background p-4 dark:border-dark-divider-border border rounded-md dark:text-white"
                @mouseenter="handleTaskEnter(task)"
                @mouseleave="handleTaskLeave">
                <div class="flex items-center">
                  <div
                    :class="[
                      `shadow-lg ${
                        task.status == 'pending' &&
                        $moment.utc(task.due_date ? task.due_date : '3000-01-01').format('YYYY-MM-DD') >=
                        $moment
                          .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                          .format('YYYY-MM-DD')
                          ? 'bg-status-warning-yellow'
                          : task.status == 'completed'
                            ? 'bg-status-success-green'
                            : task.status == 'cancelled' ||
                              (task.status == 'pending' &&
                                $moment.utc(task.due_date ? task.due_date : '3000-01-01').format('YYYY-MM-DD') <
                                $moment
                                  .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                                  .format('YYYY-MM-DD'))
                              ? 'bg-status-error-red'
                              : 'bg-dark-brand-blue'
                      } p-2 px-8  rounded-full mt-1 mr-4 relative`
                    ]">
                    <Menu as="div" class="flex-shrink-0 relative">
                      <MenuButton
                        :class="[
                          `${
                            task.status == 'pending' &&
                            $moment.utc(task.due_date ? task.due_date : '3000-01-01').format('YYYY-MM-DD') >=
                            $moment
                              .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                              .format('YYYY-MM-DD')
                              ? 'text-common-grey'
                              : 'text-white'
                          }`
                        ]"
                        class="inline-flex w-24 items-center justify-center border-1 rounded-full focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
                        <span class="capitalize">{{
                          task.status == "pending"
                            ? $moment.utc(task.due_date ? task.due_date : "3000-01-01").format("YYYY-MM-DD") <
                              $moment
                                .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                                .format("YYYY-MM-DD")
                              ? "Overdue"
                              : "Pending"
                            : task.status
                        }}</span>
                      </MenuButton>
                    </Menu>
                  </div>
                  <div>{{ task.name }}</div>
                  <div class="absolute -right-3 -top-3 flex gap-x-2">
                    <div
                      class="has-tooltip cursor-pointer w-7 h-7 dark:border-dark-divider-border bg-white rounded-full border dark:bg-dark-screen-background"
                      @click="handleExpandTask(task)">
                      <div class="w-[15px] h-[15px] ml-[5px] mt-[0px] border-b-[3px] border-b-[#45D72D]" />
                      <span
                        class="tooltip inline-flex items-center px-2 py-1 rounded-md text-sm bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                        {{ $t("Minimize") }}</span>
                    </div>
                  </div>
                </div>
                <div class="flex mt-5 w-full justify-between">
                  <div>
                    <div class="flex flex-row gap-2 items-center">
                      <div class="text-common-grey">
                        {{ $t("Priority") }}:
                      </div>
                      <div class="w-1/12 items-start flex justify-start text-start py-2 whitespace-nowrap text-md">
                        <div v-if="task.priority" class="capitalize flex gap-x-2 items-center">
                          <div>
                            {{ task.priority }}
                          </div>
                          <div>
                            <div v-if="task.priority == 'normal'">
                              <div :class="[`border-b-2 w-3  border-status-warning-yellow`]" />
                              <div :class="[`border-b-2 w-3 mt-1 border-status-warning-yellow`]" />
                            </div>
                            <div v-else-if="task.priority == 'high'">
                              <ChevronRight class="h-5 w-5 -rotate-90 text-status-error-red" />
                            </div>
                            <div v-else-if="task.priority == 'highest'">
                              <ChevronsRight class="h-5 w-5 -rotate-90 text-status-error-red" />
                            </div>
                            <div v-else-if="task.priority == 'low'">
                              <ChevronRight class="h-5 w-5 rotate-90 text-status-success-green" />
                            </div>
                            <div v-else-if="task.priority == 'lowest'">
                              <ChevronsRight class="h-5 w-5 rotate-90 text-status-success-green" />
                            </div>
                          </div>
                        </div>
                        <span v-else> - </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div class="flex flex-row gap-2 items-center">
                      <div class="text-common-grey">
                        {{ $t("Due Date") }}:
                      </div>
                      <div class="w-1/12 items-start flex justify-start text-start py-2 whitespace-nowrap text-md">
                        <span v-if="task.due_date">
                          {{ $moment(task.due_date).format("DD MMM  YYYY") }}
                        </span>
                        <span v-else> - </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div class="flex flex-row gap-2 items-center">
                      <div class="text-common-grey">
                        {{ $t("Related Goal") }}:
                      </div>
                      <div v-if="task?.goal" class="text-md w-24 overflow-hidden overflow-ellipsis whitespace-nowrap">
                        {{ $store.state.data.goals.results.find((g) => g.id == task.goal)?.name }}
                      </div>
                      <div v-else>
                        -
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="mb-4 flex align-middle relative bg-white dark:bg-dark-box-background p-4 dark:border-dark-divider-border border rounded-md dark:text-white"
                @mouseenter="handleTaskEnter(task)"
                @mouseleave="handleTaskLeave">
                <div
                  :class="[
                    `${
                      task.status == 'pending' &&
                      $moment.utc(task.due_date ? task.due_date : '3000-01-01').format('YYYY-MM-DD') >=
                      $moment.utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`).format('YYYY-MM-DD')
                        ? 'bg-status-warning-yellow'
                        : task.status == 'completed'
                          ? 'bg-status-success-green'
                          : task.status == 'cancelled' ||
                            (task.status == 'pending' &&
                              $moment.utc(task.due_date ? task.due_date : '3000-01-01').format('YYYY-MM-DD') <
                              $moment
                                .utc(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`)
                                .format('YYYY-MM-DD'))
                            ? 'bg-status-error-red'
                            : 'bg-dark-brand-blue'
                    }  w-4 h-4 rounded-full mt-1 mr-4`
                  ]" />
                <div>{{ task.name }}</div>
                <div class="absolute -right-3 -top-3 flex gap-x-2">
                  <div
                    class="has-tooltip cursor-pointer w-7 h-7 dark:border-dark-divider-border bg-white rounded-full border dark:bg-dark-screen-background"
                    @click="handleExpandTask(task)">
                    <div class="w-[15px] h-[15px] ml-[5px] mt-[5px] border-[3px] rounded-md border-[#45D72D]" />
                    <span
                      class="tooltip inline-flex items-center px-2 py-1 rounded-md text-sm bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
                      {{ $t("Expand") }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="w-full flex items-center">
          <div v-if="tasks_count > 6" class="text-common-grey mr-2 text-sm">
            {{ $t("Show") }}
          </div>
          <SelectInput
            v-if="tasks_count > 6"
            :value="tasksPerPage"
            :is-box-background="true"
            class="w-[115px]"
            :input-classes="'text-left'"
            name="tasks_page_show_options"
            :allow-null="false"
            :items="tasks_page_show_options"
            @update:currentItem="tasksPerPage = $event" />
          <div v-if="tasks_count > 6" class="text-common-grey ml-2 text-sm">
            {{ $t("in page") }}
          </div>
          <Pagination
            v-if="tasks_count > tasksPerPage"
            class="ml-auto"
            :total-pages="tasks_total_pages"
            :current-page="tasksCurrentPage"
            @input="tasksChangePage" />
        </div>
        <div
          class="grid grid-cols-3 gap-3 no-scrollbar shadow-none rounded-lg dark:bg-dark-screen-background transition ease-in-out duration-side-menu-transition-duration">
          <div v-for="(note, index) in notes_displayed" :key="note">
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95">
              <div class="relative" @mouseenter="note_hover_index = index" @mouseleave="note_hover_index = null">
                <div v-if="index == note_hover_index" class="absolute right-2 flex gap-x-2 top-2 cursor-pointer">
                  <Pencil
                    :color="$store.state.ui.darkMode ? '#fff' : '#899399'"
                    class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration"
                    @click="editNote(note)" />
                  <Trash2
                    :color="$store.state.ui.darkMode ? '#fff' : '#899399'"
                    class="dark:text-white w-5 h-5 transition ease-in-out duration-side-menu-transition-duration"
                    @click="deleteNote(note)" />
                </div>
                <div class="border dark:border-dark-divider-border rounded-lg p-3 bg-white dark:bg-dark-box-background">
                  <div class="dark:text-white font-semibold text-lg">
                    {{ note.title }}
                  </div>
                  <TextEditor
                    :disabled="true"
                    :no-border="true"
                    :value="note.description"
                    toolbar="essential"
                    :name="`description${index}`"
                    label=""
                    theme="bubble" />
                  <!-- <div class="mt-3 mbn-2" v-html="note.description" /> -->
                </div>
              </div>
            </transition>
          </div>
        </div>
        <div class="w-full flex items-center">
          <div v-if="notes_count > 3" class="text-common-grey mr-2 text-sm">
            {{ $t("Show") }}
          </div>
          <SelectInput
            v-if="notes_count > 3"
            :value="notesPerPage"
            :is-box-background="true"
            class="w-[115px]"
            :input-classes="'text-left'"
            name="notes_page_show_options"
            :allow-null="false"
            :items="notes_page_show_options"
            @update:currentItem="notesPerPage = $event" />
          <div v-if="notes_count > 3" class="text-common-grey ml-2 text-sm">
            {{ $t("in page") }}
          </div>
          <Pagination
            v-if="notes_count > notesPerPage"
            class="ml-auto"
            :total-pages="notes_total_pages"
            :current-page="notesCurrentPage"
            @input="notesChangePage" />
        </div>
        <AttachmentsField v-model="attachments" :disabled="true" />
      </div>
    </div>
  </div>
</template>
<script>
import { DatePicker } from "v-calendar"
import TextInput from "@/components/shared/TextInput.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import TextEditor from "@/components/shared/TextEditor.vue"
import dataService from "@/services/data.service"
import ContactsSelect from "@/components/shared/ContactsSelect.vue"
import TeamMembersSelect from "@/components/shared/TeamMembersSelect.vue"
import CompaniesSelect from "@/components/shared/CompaniesSelect.vue"
import { ChevronsRight, ChevronRight, Trash2, Pencil, ArrowLeft } from "lucide-vue-next"
import Pagination from "@/components/shared/Pagination.vue"
import { Menu, MenuButton } from "@headlessui/vue"
import AttachmentsField from "@/components/shared/form-inputs/AttachmentsField.vue"
import ToggleStatusButton from "../shared/ToggleStatusButton.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    TextEditor,
    ContactsSelect,
    TeamMembersSelect,
    CompaniesSelect,
    TextInput,
    DatePicker,
    SelectInput,
    ChevronRight,
    ChevronsRight,
    Pagination,
    Pencil,
    Trash2,
    Menu,
    MenuButton,
    AttachmentsField,
    ArrowLeft,
    ToggleStatusButton
  },
  props: {
    plan: {
      type: Object,
      required: true
    },
    planValues: {
      type: Object,
      required: false
    }
  },
  emits: ["cancel", "submitted", "error", "done"],
  data() {
    return {
      expanded_goals: [],
      expanded_tasks: [],
      isBoxBackgroud: false,
      types: [
        { label: this.$t("Account Plan"), value: "account_plan" },
        { label: this.$t("Growth Plan"), value: "growth_plan" },
        { label: this.$t("Success Plan"), value: "success_plan" },
        { label: this.$t("Onboarding Plan"), value: "onboarding_plan" },
        { label: this.$t("Health Plan"), value: "health_plan" },
        { label: this.$t("Renewal Plan"), value: "renewal_plan" },
        { label: this.$t("Other"), value: "other" }
      ],
      note_to_be_deleted: null,
      note_to_be_edited: null,
      is_delete_note_modal_open: false,
      note_title: null,
      note_text: null,
      is_editing_note: false,
      selected_companies: [],
      next_contacts_list_link: "",
      selected_team_members: [],
      contacts_list: [],
      contacts_list_backup: [],
      selected_contacts: [],
      due_date: null,
      inputIsFocused: false,
      goal_hover_id: null,
      task_hover_id: null,
      perPage: 6,
      currentPage: 1,
      related_goals_displayed: [],
      total_pages: 1,
      goals_count: 0,
      tasksPerPage: 6,
      tasksCurrentPage: 1,
      related_tasks_displayed: [],
      tasks_total_pages: 1,
      tasks_count: 0,
      notesPerPage: 3,
      notesCurrentPage: 1,
      notes_displayed: [],
      notes_total_pages: 1,
      notes_count: 0,
      note_hover_index: null,
      status: null,
      full_screen: true,
      attachments: []
    }
  },
  computed: {
    page_show_options() {
      const options = [6]
      if (this.goals_count > 6) {
        options.push(8)
      }
      if (this.goals_count > 8) {
        options.push(10)
      }
      if (this.goals_count > 10) {
        options.push(12)
      }
      return options
    },
    tasks_page_show_options() {
      const options = [6]
      if (this.tasks_count > 6) {
        options.push(8)
      }
      if (this.tasks_count > 8) {
        options.push(10)
      }
      if (this.tasks_count > 10) {
        options.push(12)
      }
      return options
    },
    notes_page_show_options() {
      const options = [3]
      if (this.notes_count > 3) {
        options.push(6)
      }
      if (this.notes_count > 6) {
        options.push(9)
      }
      if (this.notes_count > 9) {
        options.push(12)
      }
      if (this.notes_count > 12) {
        options.push(15)
      }
      return options
    }
  },
  watch: {
    perPage: {
      handler() {
        const params = { page_size: this.perPage, page: 1 }
        dataService.getPlanGoals(this.plan.id, params).then((res) => {
          this.currentPage = 1
          this.related_goals_displayed = res.data.results.map((g) => {
            return {
              ...g,
              filter: g.filter?.split(" ")[0]
                ? g.filter.split(" ")[0].length > 1
                  ? `( ${g.filter.slice(1, g.filter.lenght - 1)} )`
                  : g.filter
                : g.filter
            }
          })
          this.totalPages(res.data)
        })
      }
    },
    tasksPerPage: {
      handler() {
        const params = { page_size: this.tasksPerPage, page: 1 }
        dataService.getPlanTasks(this.plan.id, params).then((res) => {
          this.tasksCurrentPage = 1
          this.related_tasks_displayed = res.data.results
          this.tasksTotalPages(res.data)
        })
      }
    },
    notesPerPage: {
      handler() {
        const params = { page_size: this.notesPerPage, page: 1 }
        dataService.getPlanNotes(this.plan.id, params).then((res) => {
          this.notesCurrentPage = 1
          this.notes_displayed = res.data.results
          this.notesTotalPages(res.data)
        })
      }
    }
  },
  unmounted() {
    this.unregister_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },
  created() {
    this.register_listeners()
    this.attachments = this.plan.attachments.results
    if (this.planValues) {
      this.due_date = this.planValues.due_date
      this.selected_companies = this.planValues.selected_companies
      this.selected_team_members = this.planValues.selected_team_members
      this.selected_contacts = this.planValues.selected_contacts
      this.related_goals_displayed = this.planValues.related_goals_displayed
      this.related_tasks_displayed = this.planValues.related_tasks_displayed
      this.notes_displayed = this.planValues.notes_displayed
    }
    this.totalPages(this.plan.goals.results)
    this.tasksTotalPages(this.plan.tasks.results)
    this.notesTotalPages(this.plan.notes.results)
    this.selected_companies = this.plan.companies
    this.getContacts(this.selected_companies)
    this.selected_contacts = this.plan.contacts
    this.selected_team_members = this.plan.collaborators
    this.status = this.plan.status
    this.related_goals_displayed = this.plan.goals.results.map((g) => {
      return {
        ...g,
        filter: g.filter?.split(" ")[0]
          ? g.filter.split(" ")[0].length > 1
            ? `( ${g.filter.slice(1, g.filter.lenght - 1)} )`
            : g.filter
          : g.filter
      }
    })
    this.related_tasks_displayed = this.plan.tasks.results
    this.notes_displayed = this.plan.notes.results

    this.due_date = this.plan.due_date
  },
  activated() {
    this.register_listeners()
  },
  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.ADD_SELECTED_GOALS_TO_PLAN, () => {
        this.changePage(1, true)
      })
      this.$emitter.on(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_PLAN, () => {
        this.changePage(1, true)
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.ADD_SELECTED_GOALS_TO_PLAN)
      this.$emitter.off(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_PLAN)
      // this.$emitter.off("goalUpdated")
      // this.$emitter.off("taskUpdated")
    },

    changePage(page, calcTotalPages) {
      this.currentPage = page
      const params = { page_size: this.perPage, page: page }
      dataService.getPlanGoals(this.plan.id, params).then((res) => {
        this.related_goals_displayed = res.data.results.map((g) => {
          return {
            ...g,
            filter: g.filter?.split(" ")[0]
              ? g.filter.split(" ")[0].length > 1
                ? `( ${g.filter.slice(1, g.filter.lenght - 1)} )`
                : g.filter
              : g.filter
          }
        })
        if (calcTotalPages) {
          this.totalPages(res.data)
        }
      })
    },
    tasksChangePage(page, calcTotalPages) {
      this.tasksCurrentPage = page
      const params = { page_size: this.tasksPerPage, page: page }
      dataService.getPlanTasks(this.plan.id, params).then((res) => {
        this.related_tasks_displayed = res.data.results
        if (calcTotalPages) {
          this.tasksTotalPages(res.data)
        }
      })
    },
    notesChangePage(page, calcTotalPages) {
      this.notesCurrentPage = page
      const params = { page_size: this.notesPerPage, page: page }
      dataService.getPlanNotes(this.plan.id, params).then((res) => {
        this.notes_displayed = res.data.results
        if (calcTotalPages) {
          this.notesTotalPages(res.data)
        }
      })
    },
    totalPages(goals) {
      this.total_pages = Math.ceil(parseInt(goals.count) / parseInt(this.perPage))
      this.goals_count = goals.count
    },
    tasksTotalPages(tasks) {
      this.tasks_total_pages = Math.ceil(parseInt(tasks.count) / parseInt(this.tasksPerPage))
      this.tasks_count = tasks.count
    },
    notesTotalPages(notes) {
      this.notes_total_pages = Math.ceil(parseInt(notes.count) / parseInt(this.notesPerPage))
      this.notes_count = notes.count
    },
    handleGoalEnter(goal) {
      this.$utils.debounce(() => {
        this.goal_hover_id = goal.id
      }, 600)
    },
    handleGoalLeave() {
      this.goal_hover_id = null
      this.$utils.debounce(() => {
        this.goal_hover_id = null
      }, 600)
    },
    handleTaskEnter(task) {
      this.$utils.debounce(() => {
        this.task_hover_id = task.id
      }, 600)
    },
    handleTaskLeave() {
      this.task_hover_id = null
      this.$utils.debounce(() => {
        this.task_hover_id = null
      }, 600)
    },
    handleExpandGoal(goal) {
      let index = this.expanded_goals.findIndex((g) => g == goal.id)
      if (index !== -1) {
        this.expanded_goals.splice(index, 1)
      } else {
        this.expanded_goals.push(goal.id)
      }
    },
    handleExpandTask(task) {
      let index = this.expanded_tasks.findIndex((t) => t == task.id)
      if (index !== -1) {
        this.expanded_tasks.splice(index, 1)
      } else {
        this.expanded_tasks.push(task.id)
      }
    },

    editNote(note) {
      this.note_to_be_edited = note
      this.is_editing_note = true
      this.note_title = note.title
      this.note_text = note.description
    },
    deleteNote(note) {
      this.note_to_be_deleted = note
      this.is_delete_note_modal_open = true
    },
    focusInput() {
      this.inputIsFocused = !this.inputIsFocused
    },
    getContacts(companies) {
      this.contacts_list = []
      companies.map((company) => {
        let params = { type: "contact", parent: company.id, page_size: 7 }
        dataService.getRecords(params).then((result) => {
          this.next_contacts_list_link = result.data.next
          result.data.results.map((user) => {
            let first_name = user.values[this.$store.state.integration.metrics_mapping.contact_first_name_metric]
            let last_name = user.values[this.$store.state.integration.metrics_mapping.contact_last_name_metric]
            first_name = first_name == null ? "" : first_name
            last_name = last_name == null ? "" : last_name
            this.contacts_list.push({ id: user.id, full_name: first_name + " " + last_name })
          })
          this.contacts_list_backup = [...this.contacts_list]
        })
      })
    }
  }
}
</script>
